import { useEffect, useState } from "react";

/**
 * Uhr des Servers auf dem Gerät (ADR-0017 „devices carry their clock offset“, ADR-0030): Das Gerät fragt mit Netz alle
 * paar Sekunden `GET /api/serverzeit` und merkt sich die Abweichung seiner Uhr. Ohne Netz rechnet es mit der zuletzt
 * bekannten Abweichung weiter — so wechselt der Jagdzustand nach Serverzeit, auch wenn die Geräteuhr falsch geht
 * (N-49). Ein fehlgeschlagener Abgleich oder `offline` beendet den Kontakt; „kein Kontakt seit“ ist die Serverzeit
 * dieses Moments (F-089). Alles im Fensterkontext (ADR-0027, ADR-0030 „Device side“).
 *
 * Die zuletzt bekannte Abweichung bleibt im `localStorage`, damit die App schon beim Start (vor dem ersten Abgleich,
 * auch ohne Netz) nach Serverzeit entscheidet, ob Offline-Daten eines vergangenen Jagdtags zu löschen sind (K31).
 */

export interface Serverkontakt {
  /** Serverzeit − Gerätezeit in ms */
  abweichung: number;
  /** Serverzeit des letzten erfolgreichen Abgleichs */
  letzterAbgleich: Date | null;
  /** Serverzeit, seit der das Gerät keinen Kontakt hat; `null` mit Kontakt */
  keinKontaktSeit: Date | null;
}

const TAKT_MS = 3000;
const ZEITGRENZE_MS = 2500;

const ABWEICHUNG_SCHLUESSEL = "dj-abweichung";

function gemerkteAbweichung(): number {
  const w = Number(globalThis.localStorage?.getItem(ABWEICHUNG_SCHLUESSEL));
  return Number.isFinite(w) ? w : 0;
}

let stand: Serverkontakt = { abweichung: gemerkteAbweichung(), letzterAbgleich: null, keinKontaktSeit: null };
const hoerer = new Set<(s: Serverkontakt) => void>();
let takt: number | null = null;
let laeuft = false;

function setzen(neu: Partial<Serverkontakt>): void {
  stand = { ...stand, ...neu };
  for (const h of hoerer) h(stand);
}

/** Stand des Abgleichs außerhalb von React (Offline-Daten des Jagdtags). */
export function serverkontakt(): Serverkontakt {
  return stand;
}

export function serverJetzt(): Date {
  return new Date(Date.now() + stand.abweichung);
}

function kontaktVerloren(): void {
  if (!stand.keinKontaktSeit) setzen({ keinKontaktSeit: serverJetzt() });
}

async function abgleichen(): Promise<void> {
  if (laeuft) return;
  laeuft = true;
  const abbruch = new AbortController();
  const t = window.setTimeout(() => abbruch.abort(), ZEITGRENZE_MS);
  try {
    const vorher = Date.now();
    const r = await fetch("/api/serverzeit", { cache: "no-store", signal: abbruch.signal });
    const nachher = Date.now();
    const k = (await r.json()) as { serverzeit?: string };
    const server = k.serverzeit ? new Date(k.serverzeit) : null;
    if (!r.ok || !server || Number.isNaN(server.getTime())) throw new Error("keine Serverzeit");
    const abweichung = server.getTime() - (vorher + nachher) / 2;
    localStorage.setItem(ABWEICHUNG_SCHLUESSEL, String(Math.round(abweichung)));
    setzen({ abweichung, letzterAbgleich: server, keinKontaktSeit: null });
  } catch {
    kontaktVerloren();
  } finally {
    window.clearTimeout(t);
    laeuft = false;
  }
}

function online(): void {
  void abgleichen();
}

function starten(): void {
  if (takt !== null) return;
  window.addEventListener("online", online);
  window.addEventListener("offline", kontaktVerloren);
  if (!navigator.onLine) kontaktVerloren();
  void abgleichen();
  takt = window.setInterval(() => {
    if (navigator.onLine) void abgleichen();
    else kontaktVerloren();
  }, TAKT_MS);
}

function anhalten(): void {
  if (takt === null) return;
  window.clearInterval(takt);
  takt = null;
  window.removeEventListener("online", online);
  window.removeEventListener("offline", kontaktVerloren);
}

export function useServerkontakt(): Serverkontakt {
  const [s, setS] = useState(stand);
  useEffect(() => {
    hoerer.add(setS);
    starten();
    setS(stand);
    return () => {
      hoerer.delete(setS);
      if (hoerer.size === 0) anhalten();
    };
  }, []);
  return s;
}
